import React from "react";
import { chipClass } from "./index";

/**
 * Day-over-day delta primitives (shared kit so Risk / Portfolio / Market can all
 * use one consistent "vs prior session" treatment).
 *
 * HARD RULE (see cockpit/stress.ts): direction is shown by SIGN + ARROW only,
 * never by color. Color stays reserved for the stress ramp (magnitude/severity).
 * A drawdown deepening reads as a down-arrow, NOT a red-recolored tile. So
 * DeltaChip is always rendered with the quiet "neutral" chip tone.
 */

export type DeltaFormat = "usd" | "pp" | "num";

const ARROW_UP = "▲"; // ▲
const ARROW_DOWN = "▼"; // ▼
const ARROW_FLAT = "→"; // →

const isNum = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);

/** Round a delta to the precision it will be DISPLAYED at, keeping it in RAW
 *  units (fmtAbs does the *100 for pp), so flat-detection matches the rendered
 *  magnitude (a tiny non-zero value shows as flat). */
function roundForFormat(delta: number, format: DeltaFormat, digits: number): number {
  if (format === "usd") return Math.round(delta);
  const scale = Math.pow(10, digits);
  // pp is shown as percentage points (value*100), so round at (digits+2) places.
  if (format === "pp") return Math.round(delta * 100 * scale) / (scale * 100);
  return Math.round(delta * scale) / scale;
}

function fmtAbs(value: number, format: DeltaFormat, digits: number): string {
  const abs = Math.abs(value);
  if (format === "usd") return `$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (format === "pp") return `${(abs * 100).toFixed(digits)}pp`;
  return abs.toFixed(digits);
}

// ---- Signed inline formatters (for use outside a chip) ---------------------

export const fmtSignedUSD = (n?: number | null): string => {
  if (!isNum(n)) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export const fmtSignedPP = (n?: number | null, digits = 1): string => {
  if (!isNum(n)) return "—";
  const pp = n * 100;
  const sign = pp > 0 ? "+" : pp < 0 ? "−" : "";
  return `${sign}${Math.abs(pp).toFixed(digits)}pp`;
};

export const fmtSignedNum = (n?: number | null, digits = 2): string => {
  if (!isNum(n)) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${Math.abs(n).toFixed(digits)}`;
};

// ---- Delta chip ------------------------------------------------------------

export interface DeltaChipProps {
  /** current minus prior. When null/undefined, NOTHING renders (no fake 0). */
  delta?: number | null;
  format?: DeltaFormat;
  digits?: number;
  /** Tooltip — e.g. "vs prior session (Fri 2026-06-13)". */
  title?: string;
  /** Shown when the delta rounds to zero at display precision. */
  zeroLabel?: string;
  /** When true, render nothing if the delta rounds to zero (e.g. funded % on a
   *  no-trade day, where a daily "flat" chip would just be noise). */
  hideZero?: boolean;
  className?: string;
}

/**
 * A quiet pill showing an arrow + magnitude. Renders null when `delta` is not a
 * finite number, so a missing prior session shows no chip rather than "0".
 */
export const DeltaChip: React.FC<DeltaChipProps> = ({
  delta,
  format,
  digits = 2,
  title,
  zeroLabel = "flat",
  hideZero = false,
  className = "",
}) => {
  if (!isNum(delta)) return null;
  const fmt: DeltaFormat = format ?? "num";
  const rounded = roundForFormat(delta, fmt, digits);
  const dir = rounded > 0 ? 1 : rounded < 0 ? -1 : 0;
  if (hideZero && dir === 0) return null;
  const arrow = dir > 0 ? ARROW_UP : dir < 0 ? ARROW_DOWN : ARROW_FLAT;
  const label = dir === 0 ? zeroLabel : fmtAbs(rounded, fmt, digits);
  return (
    <span className={chipClass("neutral", className)} title={title}>
      <span aria-hidden="true">{arrow}</span>
      {label}
    </span>
  );
};

/**
 * A small "▲ X  vs prior session" line for a tile subtext. Renders null when
 * there is no comparison value.
 */
export const DeltaRow: React.FC<DeltaChipProps & { windowLabel?: string }> = ({
  windowLabel = "vs prior session",
  ...chip
}) => {
  if (!isNum(chip.delta)) return null;
  // Mirror DeltaChip's hideZero so the whole row (incl. label) disappears.
  if (chip.hideZero) {
    const rounded = roundForFormat(chip.delta, chip.format ?? "num", chip.digits ?? 2);
    if (rounded === 0) return null;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <DeltaChip {...chip} />
      <span style={{ color: "var(--text-muted)" }}>{windowLabel}</span>
    </span>
  );
};
